/**
 * PropostaInviataFields — campi per lo stage `proposta_inviata`:
 * libreria proposte (Fase 31 D), importo obbligatorio con preset, link e data.
 * ★ Fase 59.P2 — estratto da StageTransitionModal.jsx. Comportamento invariato.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import i18n from '../../i18n';

const COUNTRY_OPTS = [
  { value: 'albania', label: '🇦🇱 Albania' },
  { value: 'indonesia', label: '🇮🇩 Indonesia' },
  { value: 'thailandia', label: '🇹🇭 Thailandia' },
];
const PATH_OPTS = [
  { value: 'equity', label: 'Equity' },
  { value: 'real_estate', label: 'Real Estate' },
];
// ★ 2026-07-03: importo obbligatorio con preset rapidi
const AMOUNT_PRESETS = [
  { label: '€ 10k', v: 10000 },
  { label: '€ 25k', v: 25000 },
  { label: '€ 50k', v: 50000 },
  { label: '€ 100k', v: 100000 },
  { label: '€ 250k', v: 250000 },
  { label: '€ 500k', v: 500000 },
  { label: '€ 1M', v: 1000000 },
  { label: '€ 2.5M', v: 2500000 },
];

const fmtEur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

export function PropostaInviataFields({
  form, set,
  proposalsLibrary, loadingLibrary,
  selectedCountries, setSelectedCountries,
  selectedPaths, setSelectedPaths,
  selectedProposalIds, setSelectedProposalIds,
}) {
  const filteredLibrary = proposalsLibrary.filter((p) => {
    const cOK = selectedCountries.length === 0 || selectedCountries.includes(p.country);
    const pOK = selectedPaths.length === 0 || selectedPaths.includes(p.path);
    return cOK && pOK;
  });
  const selectedItems = proposalsLibrary.filter((p) => selectedProposalIds.includes(p.id));
  const totalSelected = selectedItems.reduce((sum, p) => sum + (Number(p.price_eur) || 0), 0);
  const toggleCountry = (c) => setSelectedCountries((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  const togglePath = (p) => setSelectedPaths((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  const toggleProposal = (pid) => {
    setSelectedProposalIds((prev) => {
      const next = prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid];
      // Auto-fill importo proposta con la somma
      const newItems = proposalsLibrary.filter((p) => next.includes(p.id));
      const sum = newItems.reduce((s, p) => s + (Number(p.price_eur) || 0), 0);
      if (next.length > 0) {
        set('proposal_amount_eur', String(sum));
      }
      return next;
    });
  };

  const currentAmount = parseFloat(form.proposal_amount_eur) || 0;
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-xs text-orange-900">
        {i18n.t('stageTransition.proposalIntro', 'Proposta inviata — l\'importo alimenta il KPI Potenziale fatturato in Dashboard. Se selezioni proposte dalla libreria, verrà creato un Ordine Proposta con le email pronte per essere inviate dagli admin CEO.')}
      </div>

      {/* ★ Fase 31 D — Libreria proposte: multi-select country/path + selezione proposte */}
      <div className="rounded-lg border-2 border-purple-300 bg-purple-50/40 p-3 space-y-3" data-testid="proposal-library-selector">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-purple-900 uppercase tracking-wide">📦 {i18n.t('stageTransition.libraryTitle', 'Libreria proposte (opzionale)')}</span>
          {selectedProposalIds.length > 0 && (
            <Badge className="text-[10px] bg-purple-600 hover:bg-purple-700">
              {selectedProposalIds.length} {i18n.t('stageTransition.selectedCount', 'selezionate')}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-purple-800">{i18n.t('stageTransition.filterCountry', 'Filtra per paese (multi)')}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {COUNTRY_OPTS.map((c) => {
                const active = selectedCountries.includes(c.value);
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => toggleCountry(c.value)}
                    className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                      active ? 'bg-purple-600 border-purple-700 text-white' : 'bg-white border-purple-200 text-purple-800 hover:bg-purple-100'
                    }`}
                    data-testid={`transition-country-${c.value}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-[11px] text-purple-800">{i18n.t('stageTransition.filterPath', 'Filtra per path (multi)')}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {PATH_OPTS.map((p) => {
                const active = selectedPaths.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePath(p.value)}
                    className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                      active ? 'bg-purple-600 border-purple-700 text-white' : 'bg-white border-purple-200 text-purple-800 hover:bg-purple-100'
                    }`}
                    data-testid={`transition-path-${p.value}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-h-[220px] overflow-auto rounded-md border bg-white">
          {loadingLibrary ? (
            <div className="p-3 text-xs text-muted-foreground italic">{i18n.t('stageTransition.loadingLibrary', 'Caricamento libreria…')}</div>
          ) : filteredLibrary.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground italic">
              {proposalsLibrary.length === 0
                ? i18n.t('stageTransition.libraryEmpty', 'Libreria vuota. Chiedi a un admin CEO di crearne una.')
                : i18n.t('stageTransition.libraryNoMatch', 'Nessuna proposta corrisponde ai filtri selezionati.')}
            </div>
          ) : (
            <ul className="divide-y">
              {filteredLibrary.map((p) => {
                const selected = selectedProposalIds.includes(p.id);
                return (
                  <li key={p.id} className={`p-2 flex items-center gap-2 cursor-pointer transition-colors ${
                    selected ? 'bg-purple-50' : 'hover:bg-slate-50'
                  }`}
                    onClick={() => toggleProposal(p.id)}
                    data-testid={`transition-proposal-item-${p.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleProposal(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {p.country} · {p.path} · {p.payment_method}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-emerald-700 whitespace-nowrap">
                      {fmtEur(p.price_eur)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {selectedProposalIds.length > 0 && (
          <div className="flex items-center justify-between p-2 bg-white rounded-md border border-purple-200">
            <span className="text-xs text-purple-800">
              {i18n.t('stageTransition.totalSelected', 'Totale {{n}} proposte selezionate:', { n: selectedProposalIds.length })}
            </span>
            <span className="text-lg font-bold text-purple-900" data-testid="transition-total-selected">
              {fmtEur(totalSelected)}
            </span>
          </div>
        )}
      </div>

      {/* ★ 2026-07-03: IMPORTO PROPOSTA — obbligatorio + preset */}
      <div className="rounded-lg border-2 border-blue-300 bg-blue-50/40 p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide font-semibold text-blue-900">
            💰 {i18n.t('stageTransition.proposalAmount', 'Importo proposta * (€)')}
          </Label>
          {currentAmount > 0 && (
            <span className="font-serif text-lg text-blue-900 tabular-nums" data-testid="proposta-amount-display">
              {fmtEur(currentAmount)}
            </span>
          )}
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {AMOUNT_PRESETS.map((p) => {
            const selected = currentAmount === p.v;
            return (
              <button
                key={p.v}
                type="button"
                onClick={() => set('proposal_amount_eur', String(p.v))}
                className={`px-2 py-2 rounded-md border-2 text-xs font-semibold transition-all ${
                  selected
                    ? 'bg-blue-600 border-blue-700 text-white shadow-md'
                    : 'bg-white border-blue-200 text-blue-800 hover:bg-blue-100 hover:border-blue-400'
                }`}
                data-testid={`proposta-amount-preset-${p.v}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">{i18n.t('stageTransition.customAmount', 'Oppure importo personalizzato:')}</Label>
          <Input
            type="number"
            min="0"
            step="1000"
            value={form.proposal_amount_eur || ''}
            onChange={(e) => set('proposal_amount_eur', e.target.value)}
            placeholder="es. 175000"
            data-testid="proposta-amount-input"
            className="mt-1 h-9 text-sm font-mono"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.proposalLink', 'Link proposta')} *</Label>
        <Input
          value={form.proposta_url}
          onChange={(e) => set('proposta_url', e.target.value)}
          placeholder="https://drive.google.com/…"
          data-testid="proposta-url-input"
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide">{i18n.t('stageTransition.sentDate', 'Data invio')} *</Label>
        <Input
          type="datetime-local"
          value={form.proposta_sent_at}
          onChange={(e) => set('proposta_sent_at', e.target.value)}
          data-testid="proposta-sent-at-input"
          className="mt-1"
        />
      </div>
    </div>
  );
}
